import { Link } from 'react-router';
import { UilLock } from '@iconscout/react-unicons';
import { Button } from '../buttons/Button';
import Skeleton from '../Skeleton';
import { useAuth } from '../../lib/auth';
import { usePlan } from '../../lib/plan/usePlan';
import {
    PlanDay,
    PlanResponse,
    PlanWeek,
    themeLabel,
} from '../../lib/plan/types';
import { parseTime } from '../../lib/helpers';

// Najde den označený jako aktuální napříč všemi týdny plánu.
function findCurrentDay(
    weeks: PlanWeek[]
): { day: PlanDay; week: PlanWeek } | null {
    for (const week of weeks) {
        const day = week.days.find((d) => d.is_current);
        if (day) return { day, week };
    }
    return null;
}

function formatLength(length: number): string {
    const { minutes, seconds } = parseTime(length);
    return `${minutes > 0 ? `${minutes} min ` : ''}${seconds} s`;
}

// Náhled „Zítra" — další cvičení po dnešním, nebo zamčený týden (demo).
function nextLabel(
    plan: PlanResponse,
    currentWeek: PlanWeek
): { name: string; length: number | null } | null {
    const nextPosition = (plan.current_day_position ?? 0) + 1;
    if (nextPosition > plan.total_positions) return null;

    for (const week of plan.weeks) {
        const day = week.days.find((d) => d.position === nextPosition);
        if (day?.lecture) {
            return { name: day.lecture.name, length: day.lecture.video.length };
        }
    }

    // Zítřek je v zamčeném týdnu — máme jen téma.
    const nextWeek = plan.weeks.find((w) => w.week === currentWeek.week + 1);
    const theme = themeLabel(nextWeek?.theme_key ?? null);
    return {
        name: `Týden ${currentWeek.week + 1} · ${theme}`,
        length: null,
    };
}

export function PlanTodayCard() {
    const { userHasFullLicense } = useAuth({ tmp: 'PlanTodayCard' });
    const { plan, isLoading } = usePlan();

    if (isLoading) {
        return <Skeleton className="h-40 w-full rounded-2xl" />;
    }

    // Stav 1: bez plánu — pozvánka k sestavení plánu na míru.
    if (plan === null) {
        return (
            <div className="rounded-2xl bg-white p-6 text-center shadow-sm">
                <h2 className="mb-2">Sestavte si plán na míru</h2>
                <p className="mb-6 text-ink-800">
                    Odpovězte na pár otázek a získejte plán cvičení šitý přesně
                    pro vaši třídu.
                </p>
                <Link to="/plan-na-miru/dotaznik" className="inline-block">
                    <Button color="product">Sestavit plán na míru</Button>
                </Link>
            </div>
        );
    }

    // Stav 3: plán je hotový — potvrzení, žádné cvičení dnes nečeká.
    if (plan.is_complete) {
        return (
            <div className="rounded-2xl bg-white p-6 text-center shadow-sm">
                <h2 className="mb-2">Dnešek máte hotový.</h2>
                <p className="mb-6 text-ink-800">
                    Celý plán máte úspěšně za sebou. Podívejte se, co bylo
                    v plánu, nebo si projděte cvičení znovu.
                </p>
                <Link to="/plan-na-miru" className="inline-block">
                    <Button color="product" variant="secondary">
                        Zobrazit plán
                    </Button>
                </Link>
            </div>
        );
    }

    // Stav 2: plán běží — dnešní cvičení k odehrání + panel pokroku.
    const current = findCurrentDay(plan.weeks);

    const { minutes, seconds } = parseTime(
        current?.day.lecture?.video.length ?? 0
    );
    const lectureName = current?.day.lecture?.name ?? 'Dnešní cvičení';

    const totalWeeks = plan.weeks.length;
    const currentWeek = current?.week.week ?? 1;
    const progressPct =
        plan.total_positions > 0
            ? Math.round(
                  (plan.completed_positions / plan.total_positions) * 100
              )
            : 0;
    const next = current ? nextLabel(plan, current.week) : null;

    // Demo CTA: uživatel je na demo licenci, aktuální den je v týdnu 1
    // a zároveň je posledním nedokončeným dnem tohoto týdne — po jeho
    // dokončení se ukrojí i zbytek zdarma dostupného týdne.
    const isLastFreeDemoDay =
        !userHasFullLicense &&
        current?.week.week === 1 &&
        current.week.days.filter((d) => !d.is_completed).length === 1;

    return (
        <div className="rounded-2xl bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-6 md:flex-row md:items-stretch md:justify-between">
                {/* Dnešní cvičení */}
                <div className="flex-1">
                    <h4 className="mb-1">Dnešní cvičení</h4>
                    <h2 className="mb-2">{lectureName}</h2>
                    <p className="mb-6 text-ink-800">
                        {minutes > 0 && <>{minutes}&nbsp;min</>} {seconds}&nbsp;s
                    </p>
                    <div className="flex flex-wrap gap-4">
                        <Link to="/plan-na-miru" className="inline-block">
                            <Button color="product">Spustit</Button>
                        </Link>
                        {isLastFreeDemoDay && (
                            <Link to="/koupit" className="inline-block">
                                <Button color="product" variant="secondary">
                                    Odemkněte celý rok
                                </Button>
                            </Link>
                        )}
                    </div>
                </div>

                {/* Panel pokroku */}
                <div className="border-t border-cloud-200 pt-6 md:w-64 md:shrink-0 md:border-l md:border-t-0 md:pl-6 md:pt-0">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-700">
                        Plán na míru
                    </p>
                    <p className="mb-1">
                        <span className="text-3xl font-bold text-product-600">
                            {plan.completed_positions}
                        </span>
                        <span className="text-lg text-ink-700">
                            {' '}
                            / {plan.total_positions} cvičení
                        </span>
                    </p>
                    <p className="mb-4 text-sm text-ink-800">
                        Týden {currentWeek} z {totalWeeks}
                    </p>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-cloud-200">
                        <div
                            className="h-full rounded-full bg-product-500 transition-all"
                            style={{ width: `${progressPct}%` }}
                        />
                    </div>
                </div>
            </div>

            {next && (
                <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-cloud-200 pt-4 text-sm text-ink-800">
                    <UilLock size={16} className="shrink-0 text-ink-700" />
                    <span className="font-medium">Zítra vás čeká:</span>
                    <span>{next.name}</span>
                    {next.length !== null && (
                        <span className="text-ink-700">
                            · {formatLength(next.length)}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}
